import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import { ToastSettings } from "../../utils/ToastSettings";
import { ChatHighlightsService } from "../../services/ChatHighlights/ChatHighlightsService";

interface ContextProps {
  highlights: any[];
  activeHighlight: any | null;
  currentHighlight: any | null;
  showHighlightModal: boolean;
  isLoading: boolean;
  error: string;
  handleList: () => void;
  handleCreate: (data: any) => Promise<boolean>;
  handleUpdate: (id: number, data: any) => Promise<boolean>;
  handleActivate: (id: number) => Promise<boolean>;
  handleDeactivate: (id: number) => Promise<boolean>;
  handleDelete: (id: number) => Promise<boolean>;
  handleGetActive: (autoOpen?: boolean) => Promise<void>;
  handleOpenModal: () => Promise<void>;
  handleCloseModal: () => void;
}

export const ChatHighlightsContext = createContext<ContextProps>(
  {} as ContextProps
);

interface ChatHighlightsProviderProps {
  children: ReactNode;
}

export const ChatHighlightsProvider = ({
  children,
}: ChatHighlightsProviderProps) => {
  const [highlights, setHighlights] = useState<any[]>([]);
  const [activeHighlight, setActiveHighlight] = useState<any | null>(null);
  const [currentHighlight, setCurrentHighlight] = useState<any | null>(null);
  const [showHighlightModal, setShowHighlightModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const service = new ChatHighlightsService();

  const handleList = async () => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list();
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return;
      }

      setHighlights(_Response?.data?.items ?? []);
      setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os destaques do chat.");
    }
  };

  const handleCreate = async (data: any): Promise<boolean> => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.create(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar o destaque.");
      return false;
    }
  };

  const handleUpdate = async (id: number, data: any): Promise<boolean> => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.update(id, data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao atualizar o destaque.");
      return false;
    }
  };

  const handleActivate = async (id: number): Promise<boolean> => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.activate(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(e.response?.data?.message || "Houve um erro ao ativar o destaque.");
      return false;
    }
  };

  const handleDeactivate = async (id: number): Promise<boolean> => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.deactivate(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(e.response?.data?.message || "Houve um erro ao desativar o destaque.");
      return false;
    }
  };

  const handleDelete = async (id: number): Promise<boolean> => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(e.response?.data?.message || "Houve um erro ao deletar o destaque.");
      return false;
    }
  };

  const handleGetActive = async (autoOpen: boolean = true): Promise<void> => {
    try {
      if (autoOpen) {
        // Verifica se deve abrir o modal automaticamente (respeita cooldown de dismiss)
        const [_Response, _Error] = await service.getActive();
        const highlight = _Response?.data?.items?.[0] ?? _Response?.data ?? null;

        if (!_Error && highlight) {
          setActiveHighlight(highlight);
          setCurrentHighlight(highlight);
          setShowHighlightModal(true);
          return;
        }
      }

      // Sem auto-open (dispensou hoje, tutorial completo hoje, ou sem ativo):
      // ainda verifica se existe destaque para exibir o botão "+"
      const [_ForceResponse, _ForceError] = await service.getActiveForce();
      const forceHighlight = _ForceResponse?.data?.items?.[0] ?? _ForceResponse?.data ?? null;

      setActiveHighlight(!_ForceError && forceHighlight ? forceHighlight : null);
    } catch (e) {
      setActiveHighlight(null);
    }
  };

  const handleOpenModal = async (): Promise<void> => {
    try {
      const [_Response, _Error] = await service.getActiveForce();
      const highlight = _Response?.data?.items?.[0] ?? _Response?.data ?? null;

      if (_Error || !highlight) return;

      setCurrentHighlight(highlight);
      setShowHighlightModal(true);
    } catch (e) {
      // silently fail
    }
  };

  const handleCloseModal = async () => {
    if (currentHighlight?.id) {
      try {
        await service.dismiss(currentHighlight.id);
      } catch (e) {
        // dismiss errors are non-critical
      }
    }
    setShowHighlightModal(false);
    setCurrentHighlight(null);
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  return (
    <ChatHighlightsContext.Provider
      value={{
        highlights,
        activeHighlight,
        currentHighlight,
        showHighlightModal,
        isLoading,
        error,
        handleList,
        handleCreate,
        handleUpdate,
        handleActivate,
        handleDeactivate,
        handleDelete,
        handleGetActive,
        handleOpenModal,
        handleCloseModal,
      }}
    >
      {children}
    </ChatHighlightsContext.Provider>
  );
};

export const useChatHighlights = () => useContext(ChatHighlightsContext);
